import React, { useEffect, useState, useCallback } from "react";
import { useParams, useLocation } from "react-router-dom";
import { database } from "./firebase";
import { ref, get } from "firebase/database";

export default function GalleryView() {
  const { galleryId } = useParams();
  const location = useLocation();
  const [universalLink, setUniversalLink] = useState("");
  const [title, setTitle] = useState("MoodStream Gallery");
  const [imageUrl, setImageUrl] = useState("https://firebasestorage.googleapis.com/v0/b/pokefinder-9c71b.appspot.com/o/logo_img%2Fmoodsmiley.png?alt=media");
  const [thumbnail, setThumbnail] = useState("");
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [appOpening, setAppOpening] = useState(false);

  // Check if iOS
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  // Fetch gallery data from Firebase
  const fetchGalleryData = useCallback(async () => {
    if (!galleryId) return;

    try {
      setLoading(true);

      const galleryRef = ref(database, `galleries/${galleryId}`);
      const gallerySnapshot = await get(galleryRef);

      let galleryTitle = "MoodStream Gallery";
      let galleryThumbnail = "";

      if (gallerySnapshot.exists()) {
        const galleryData = gallerySnapshot.val();
        if (galleryData?.public?.galleryInfo?.name) {
          galleryTitle = galleryData.public.galleryInfo.name;
          setTitle(galleryTitle);
        }
        if (galleryData?.public?.galleryInfo?.postImgURL) {
          galleryThumbnail = galleryData.public.galleryInfo.postImgURL;
          setThumbnail(galleryThumbnail);
        }
      }

      const postsRef = ref(database, `posts/${galleryId}`);
      const postsSnapshot = await get(postsRef);

      let firstPostImage = "";
      const postsArray = [];

      if (postsSnapshot.exists()) {
        const postsData = postsSnapshot.val();
        postsArray.push(...Object.values(postsData).sort(
          (a, b) => (b.timestamp || 0) - (a.timestamp || 0)
        ));

        if (postsArray.length > 0 && postsArray[0].imgURL) {
          firstPostImage = postsArray[0].imgURL;
        }
      }

      setPosts(postsArray);
      const finalImageUrl = firstPostImage || galleryThumbnail || imageUrl;
      setImageUrl(finalImageUrl);
      setLoading(false);

      return { galleryTitle, galleryThumbnail, finalImageUrl, posts: postsArray };
    } catch (err) {
      console.error("Error fetching gallery data:", err);
      setLoading(false);
      return null;
    }
  }, [galleryId, imageUrl]);

  useEffect(() => {
    if (galleryId) {
      fetchGalleryData();
    }
  }, [galleryId, fetchGalleryData]);


  // Set up meta tags and URLs
  useEffect(() => {
    if (!galleryId) return;

    const searchParams = new URLSearchParams(location.search);
    const galleryCode = searchParams.get("galleryCode");
    const thumb = searchParams.get("thumbnail");
    const titleParam = searchParams.get("title");

    const finalTitle = titleParam ? decodeURIComponent(titleParam) : title;
    const finalThumbnail = thumb ? decodeURIComponent(thumb) : thumbnail || imageUrl;

    // Build Universal Link
    const link = `https://app.moodstream.photos/galleryInvite?galleryID=${encodeURIComponent(galleryId)}` +
      (galleryCode ? `&galleryCode=${encodeURIComponent(galleryCode)}` : "") +
      (thumb ? `&thumbnail=${encodeURIComponent(thumb)}` : "") +
      (titleParam ? `&title=${encodeURIComponent(titleParam)}` : "");

    setUniversalLink(link);

    // Set document title and meta tags
    document.title = finalTitle;

    const metaConfig = [
      { property: 'og:type', content: 'website' },
      { property: 'og:title', content: finalTitle },
      { property: 'og:description', content: 'Join my stream and post together with me!' },
      { property: 'og:image', content: finalThumbnail },
      { property: 'og:url', content: window.location.href },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: finalTitle },
      { name: 'twitter:description', content: 'Join my stream and post together with me!' },
      { name: 'twitter:image', content: finalThumbnail },
      { name: 'description', content: 'Join my stream and post together with me!' }
    ];

    metaConfig.forEach(({ property, name, content }) => {
      const selector = property
        ? `meta[property="${property}"]`
        : `meta[name="${name}"]`;

      let metaTag = document.querySelector(selector);

      if (!metaTag) {
        metaTag = document.createElement('meta');
        if (property) metaTag.setAttribute('property', property);
        if (name) metaTag.setAttribute('name', name);
        document.head.appendChild(metaTag);
      }

      metaTag.setAttribute('content', content);
    });

    return () => {
      document.title = "MoodStream";
    };
  }, [galleryId, location.search, title, thumbnail, imageUrl]);

  // Handle opening app
  const handleOpenApp = (e) => {
    e?.preventDefault();
    e?.stopPropagation();

    if (!universalLink) return;

    setAppOpening(true);

    // Direct navigation to Universal Link
    window.location.href = universalLink;

    // Reset loading after delay
    setTimeout(() => {
      setAppOpening(false);
    }, 2000);
  };

  if (loading) {
    return (
      <>
        <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>

        <div
          style={{
            fontFamily:
              "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
            background:
              "linear-gradient(135deg, #6B46C1 0%, #9333EA 50%, #EC4899 100%)",
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              width: "56px",
              height: "56px",
              border: "5px solid rgba(255,255,255,0.3)",
              borderTop: "5px solid #fff",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          />
        </div>
      </>
    );
  }

  const displayImageUrl = posts.length > 0 && posts[0]?.imgURL
    ? posts[0].imgURL
    : (thumbnail || imageUrl);

  return (
    <>
      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
          background: linear-gradient(135deg, #6B46C1 0%, #9333EA 50%, #EC4899 100%);
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }
        
        .gallery-container {
            width: 400px !important;   /* 👈 force width */
  max-width: 90vw;           /* 👈 prevent overflow on small screens */
          width: 100%;
          text-align: center;
          background: white;
          border-radius: 24px;
          padding: 40px 24px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        }
        
        .logo-container {
          margin-bottom: 24px;
        }
        
        .logo {
          width: 180px;
          height: 180px;
          margin: 0 auto;
          border-radius: 30px;
          background: white;
          display: block;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          overflow: hidden;
          position: relative;
        }
        
        .logo img {
          width: 100%;
          height: 100%;
          border-radius: 30px;
          object-fit: cover;
          object-position: center;
          display: block;
        }
        
        .gallery-container h1 {
          font-size: 26px;
          font-weight: 700;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
          margin-top: 0;
          margin-bottom: 16px;
          color: #333;
          word-break: break-word;
          line-height: 1.3;
        }
        
        .description {
          font-size: 16px;
          color: #666;
          margin-bottom: 24px;
          line-height: 1.5;
          padding: 0 10px;
        }
        
        .cta-button {
          display: inline-block;
          padding: 22px 48px;
          background: #000;
          color: #fff;
          text-decoration: none;
          border-radius: 40px;
          font-size: 20px;
          font-weight: 700;
          margin-top: 8px;
          transition: all 0.3s ease;
          border: none;
          cursor: pointer;
          width: 100%;
          position: relative;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
        }
        
        .cta-button:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
        
        .cta-button:hover:not(:disabled) {
          background: #333;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
        
        .powered-by {
          font-size: 12px;
          color: #999;
          margin-top: 24px;
          letter-spacing: 0.5px;
        }
        
        .ios-note {
          font-size: 14px;
          color: #888;
          margin-top: 8px;
          margin-bottom: 24px;
          line-height: 1.4;
        }
        
        @media (max-width: 600px) {
          .gallery-container {
            padding: 32px 20px;
            margin: 0;
            border-radius: 20px;
          }
          
          .gallery-container h1 {
            font-size: 22px;
          }
          
          .description {
            font-size: 15px;
          }
          
          .cta-button {
            padding: 20px 40px;
            font-size: 18px;
          }
          
          .logo {
            width: 160px;
            height: 160px;
          }
          
          .ios-note {
            font-size: 13px;
          }
        }
        
        @media (max-width: 380px) {
          .gallery-container {
            padding: 28px 16px;
          }
          
          .gallery-container h1 {
            font-size: 20px;
          }
          
          .description {
            font-size: 14px;
          }
          
          .cta-button {
            padding: 18px 32px;
            font-size: 17px;
          }
          
          .logo {
            width: 140px;
            height: 140px;
          }
        }
        
        /* Loading spinner */
        .spinner {
          display: inline-block;
          width: 20px;
          height: 20px;
          border: 3px solid rgba(255,255,255,.3);
          border-radius: 50%;
          border-top-color: #fff;
          animation: spin 1s ease-in-out infinite;
          margin-right: 10px;
          vertical-align: middle;
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        /* iOS specific styling */
        .ios-device {
          color: #007AFF;
          font-weight: 600;
        }
      `}</style>

      <div className="gallery-container">
        <div className="logo-container">
          <div className="logo">
            <img
              src={displayImageUrl}
              alt={title}
              onError={(e) => {
                e.target.src = "https://firebasestorage.googleapis.com/v0/b/pokefinder-9c71b.appspot.com/o/logo_img%2Fmoodsmiley.png?alt=media";
              }}
            />
          </div>
        </div>

        <h1>{title}</h1>
        {/* Main button */}
        <button
          onClick={handleOpenApp}
          className="cta-button"
          disabled={appOpening || !universalLink}
        >
          {appOpening ? (
            <>
              <span className="spinner"></span>
              OPENING...
            </>
          ) : (
            'JOIN NOW'
          )}
        </button>

        <div className="powered-by">Powered by MoodStream</div>
      </div>
    </>
  );
}